import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { InlineSelect, InlineMultiSelect } from '../InlineTable';
import { ROOM_STAT_MAPPED_TYPES } from '../../utils/mappedTypes';
import {
  updateRevenueType,
  createRoomStatMappedType,
  deleteRoomStatMappedType,
} from '../../utils/api';

// MappedTypeSelect — the "Maps To" cell on import review (modal + page).
//
// Revenue and room-stat rows land in the database against a raw code from the
// file; they only reach reports once that code's TYPE carries a mapping. Before,
// this column was read-only text, so an operator who spotted "Not Mapped" while
// reviewing an import had to leave for Revenue Types (or the Unmapped Codes
// queue) to fix it. This makes the cell an inline control, matching the Unmapped
// Codes page: same InlineTable kit select.
//
// The mapping lives on the type, not the row — assigning here applies to every
// row using that code, in this import and in every future one.
//
// The two sides map differently, and each control writes what the rollups
// actually read:
//   revenue   — revenueTypes.mappedType, one free-text value (single select)
//   roomStats — roomStatMappedTypes rows, many canonical values (multi select).
//               roomStatTypes.mappedType still exists but every rollup ignores
//               it, so writing that scalar would look like a fix and change
//               nothing.

/** Distinct revenue mapped types already in use for this location, plus session additions. */
function buildRevenueOptions(types, extra) {
  const names = new Set();
  (types || []).forEach((t) => {
    const mt = t?.mappedType == null ? '' : String(t.mappedType).trim();
    if (mt) names.add(mt);
  });
  (extra || []).forEach((n) => { if (n) names.add(n); });
  return [...names]
    .sort((a, b) => a.localeCompare(b))
    .map((n) => ({ value: n, label: n }));
}

function typeLabel(type, typeId) {
  return type?.name || type?.accountName || `#${typeId}`;
}

/** Revenue: single mappedType string on the type row. */
const RevenueMappedTypeSelect = ({ typeId, type, types, setTypes, orgId, rowActive, disabled, menuZIndex, onAssigned }) => {
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState([]);

  const options = useMemo(() => buildRevenueOptions(types, created), [types, created]);
  const mappedType = type?.mappedType ? String(type.mappedType).trim() : null;
  const name = typeLabel(type, typeId);

  const assign = async (next) => {
    const value = next == null ? null : String(next).trim() || null;
    if (value === mappedType || !orgId) return;

    setSaving(true);
    setTypes((prev) => prev.map((t) => (t.id === typeId ? { ...t, mappedType: value } : t)));
    try {
      await updateRevenueType(typeId, orgId, { mappedType: value });
      toast.success(`"${name}" → ${value}`);
      onAssigned?.(value, typeId);
    } catch (err) {
      // Revert the optimistic write — the column must never show a mapping the
      // database does not have, or the operator walks away believing it stuck.
      setTypes((prev) => prev.map((t) => (t.id === typeId ? { ...t, mappedType: type?.mappedType ?? null } : t)));
      toast.error(`Failed to map "${name}": ${err.message || 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <InlineSelect
      value={mappedType}
      options={options}
      onChange={assign}
      onCreateOption={(input) => {
        const trimmed = String(input).trim();
        setCreated((prev) => [...prev, trimmed]);
        return trimmed;
      }}
      createLabel="New mapped type"
      placeholder="Not Mapped"
      // Unmapped rows keep the control visible — that is the row needing action.
      rowActive={rowActive || !mappedType}
      disabled={disabled || saving}
      clearable={false}
      minWidth={220}
      menuZIndex={menuZIndex}
      ariaLabel={`Mapped type for ${name}`}
    />
  );
};

/** Room stats: roomStatMappedTypes rows, one per mapped type, operation 'add'. */
const RoomStatMappedTypeSelect = ({ typeId, type, setTypes, orgId, locationId, rowActive, disabled, menuZIndex, onAssigned }) => {
  const [saving, setSaving] = useState(false);

  const mappings = useMemo(() => type?.mappings || [], [type]);
  const name = typeLabel(type, typeId);

  const options = useMemo(() => {
    // Canonical list first, plus any non-standard value already mapped here so
    // an existing mapping is never silently dropped from the menu.
    const names = new Set(ROOM_STAT_MAPPED_TYPES);
    mappings.forEach((m) => { if (m?.mappedType) names.add(String(m.mappedType)); });
    return [...names].map((n) => ({ value: n, label: n }));
  }, [mappings]);

  const value = useMemo(
    () => mappings.map((m) => ({ value: m.mappedType, label: m.mappedType })),
    [mappings]
  );

  const setMappings = (next) => setTypes((prev) => prev.map((t) => (t.id === typeId ? { ...t, mappings: next } : t)));

  const apply = async (selected) => {
    if (!orgId) return;
    const nextValues = (selected || []).map((o) => o.value);
    const currentValues = mappings.map((m) => m.mappedType);
    const added = nextValues.filter((v) => !currentValues.includes(v));
    const removed = mappings.filter((m) => !nextValues.includes(m.mappedType));
    if (added.length === 0 && removed.length === 0) return;

    setSaving(true);
    try {
      let next = mappings;
      for (const mappedType of added) {
        const row = await createRoomStatMappedType({ roomStatTypeId: typeId, mappedType, operation: 'add', orgId, locationId });
        next = [...next, row];
        setMappings(next);
      }
      for (const mapping of removed) {
        await deleteRoomStatMappedType(mapping.id);
        next = next.filter((m) => m.id !== mapping.id);
        setMappings(next);
      }
      toast.success(nextValues.length ? `"${name}" → ${nextValues.join(', ')}` : `"${name}" unmapped`);
      onAssigned?.(nextValues, typeId);
    } catch (err) {
      // Partial application is possible (each mapping is its own request), so
      // reload rather than guess — the parent refetch is the source of truth.
      toast.error(`Failed to map "${name}": ${err.message || 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <InlineMultiSelect
      value={value}
      options={options}
      onChange={apply}
      placeholder="Not Mapped"
      rowActive={rowActive || mappings.length === 0}
      disabled={disabled || saving}
      minWidth={260}
      menuZIndex={menuZIndex}
      ariaLabel={`Mapped types for ${name}`}
    />
  );
};

/**
 * Props:
 *   typeId     — revenueTypeId / roomStatTypeId of the row (null → nothing to map yet)
 *   types      — the location's revenueTypes or roomStatTypes array
 *                (room stats must be loaded with includeMappings so each type carries .mappings)
 *   setTypes   — state setter for that array (optimistic update + revert on failure)
 *   orgId      — organization id (required by the write endpoints)
 *   locationId — location id (stamped on new room-stat mappings)
 *   kind       — 'revenue' | 'roomStats'
 *   rowActive  — reveal the control (row hover); unmapped rows always show it
 *   onAssigned(value, typeId) — optional post-save hook (e.g. dashboard fact refresh)
 */
const MappedTypeSelect = ({
  typeId,
  types,
  setTypes,
  orgId,
  locationId,
  kind = 'revenue',
  rowActive = false,
  disabled = false,
  menuZIndex,
  onAssigned,
}) => {
  const type = useMemo(
    () => (types || []).find((t) => t.id === typeId) || null,
    [types, typeId]
  );

  if (!typeId) return <span className="text-gray-400">—</span>;

  const shared = { typeId, type, types, setTypes, orgId, rowActive, disabled, menuZIndex, onAssigned };
  return (
    <div title={`Sets the mapping for "${typeLabel(type, typeId)}" — applies to every row using this code`}>
      {kind === 'roomStats'
        ? <RoomStatMappedTypeSelect {...shared} locationId={locationId} />
        : <RevenueMappedTypeSelect {...shared} />}
    </div>
  );
};

export default MappedTypeSelect;
